import { inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class IconsService {
    /**
     * Constructor
     */
    constructor() {
        const domSanitizer = inject(DomSanitizer);
        const matIconRegistry = inject(MatIconRegistry);

        // Register individual Material Symbols
        this.registerMaterialSymbol(matIconRegistry, domSanitizer, 'search_activity');
        this.registerMaterialSymbol(matIconRegistry, domSanitizer, 'waterfall_chart');

        // Register standalone color icons
        matIconRegistry.addSvgIcon(
            'google_color',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/standalone/google_color.svg')
        );
        matIconRegistry.addSvgIcon(
            'microsoft_color',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/standalone/microsoft_color.svg')
        );

        // Register icon sets
        matIconRegistry.addSvgIconSet(domSanitizer.bypassSecurityTrustResourceUrl('icons/material-twotone.svg'));
        matIconRegistry.addSvgIconSetInNamespace(
            'mat_outline',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/material-outline.svg')
        );
        matIconRegistry.addSvgIconSetInNamespace(
            'mat_solid',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/material-solid.svg')
        );
        matIconRegistry.addSvgIconSetInNamespace(
            'feather',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/feather.svg')
        );
        matIconRegistry.addSvgIconSetInNamespace(
            'heroicons_outline',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/heroicons-outline.svg')
        );
        matIconRegistry.addSvgIconSetInNamespace(
            'heroicons_solid',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/heroicons-solid.svg')
        );
        matIconRegistry.addSvgIconSetInNamespace(
            'heroicons_mini',
            domSanitizer.bypassSecurityTrustResourceUrl('icons/heroicons-mini.svg')
        );
    }

    /**
     * Registers a Material Symbol icon with the MatIconRegistry
     *
     * Font weight 700 used
     *
     * This method allows registration of individual Material Symbols icons
     * in both filled and outlined variants. Registered icons can be used
     * with mat-icon directives using the format:
     * - Outlined: `<mat-icon svgIcon="mat_symbols_iconName"></mat-icon>`
     * - Filled: `<mat-icon svgIcon="mat_symbols_iconName_fill"></mat-icon>`
     *
     * @param registry - The MatIconRegistry instance to register icons with
     * @param sanitizer - The DomSanitizer instance to bypass security for trusted SVG URLs
     * @param iconName - The base name of the Material Symbol icon (e.g., 'settings', 'favorite')
     * @param filled - Whether to register the filled variant of the icon. Defaults to false (outlined)
     *
     * @example
     * // Register outlined settings icon
     * this.registerMaterialSymbol(matIconRegistry, domSanitizer, 'settings');
     * // Usage: <mat-icon svgIcon="mat_symbols_settings"></mat-icon>
     *
     * @example
     * // Register filled favorite icon
     * this.registerMaterialSymbol(matIconRegistry, domSanitizer, 'favorite', true);
     * // Usage: <mat-icon svgIcon="mat_symbols_favorite_fill" class="text-red-500"></mat-icon>
     */
    private registerMaterialSymbol(
        registry: MatIconRegistry,
        sanitizer: DomSanitizer,
        iconName: string,
        filled = false
    ): void {
        const suffix = filled ? '-fill' : '';
        const svgPath = `icons/material-symbols/${iconName}${suffix}.svg`;

        registry.addSvgIcon(
            `mat_symbols_${iconName}${filled ? '_fill' : ''}`,
            sanitizer.bypassSecurityTrustResourceUrl(svgPath)
        );
    }
}